import {
    Page,
    usePromise,
} from "@keycloak/keycloak-account-ui";
import {
    PageSection,
    PageSectionVariants,
    Spinner,
    Text,
    TextContent,
    Title,
} from "@patternfly/react-core";
import {useState} from "react";
import type {TFunction} from "i18next";
import { useTranslation } from "react-i18next";
import { useEnvironmentWithDonorService } from '../../context';
import type { IAMPersonalInfoJsonSchema } from "../../donor-service";
import {digestMessage} from "../../utils/digest.ts";
import {FORMAT_DATE_ONLY, formatDate} from "../../utils/formatDate.ts";
import {JoinGeneralMeeting} from "./JoinGeneralMeeting.tsx";

type DonorInfosRepresentation = IAMPersonalInfoJsonSchema & {
    donorIdDigest: string;
}

function expiry (membershipExpiry: Date, t: TFunction, locale: string): string {
    return !membershipExpiry || isNaN(membershipExpiry.valueOf())
        ? t('membershipTrue')
        : t('membershipTrueUntil', {date: formatDate(membershipExpiry, locale, FORMAT_DATE_ONLY)});
}

export function GeneralMeetingsPage () {
    const { t } = useTranslation();
    const context = useEnvironmentWithDonorService();

    const [donorInfos, setDonorInfos] = useState<DonorInfosRepresentation>();

    usePromise(
        async () => {
            const { id, personalInformation } = await context.donorService.fetchDonorRecord();
            const donorIdDigest = await digestMessage(String(id)).catch(() => String(id));
            return {...personalInformation, donorIdDigest};
        },
        setDonorInfos,
    );

    if (!donorInfos) {
        return <Spinner />;
    }

    const { donorIdDigest, membership, membershipExpiry } = donorInfos;

    return (
        <Page title={t("generalMeetings")} description="">
            <PageSection variant={PageSectionVariants.light} padding={{ default: 'noPadding' }}>
                <TextContent>
                    <Title size="xl" headingLevel="h2">
                        {t("membership")}
                    </Title>
                    <Text>
                        { membership
                            ? expiry(new Date(membershipExpiry || ''), t, context.environment.locale)
                            : t('membershipFalse') }
                    </Text>

                    { membership && <JoinGeneralMeeting donorIdDigest={donorIdDigest} t={t} /> }
                </TextContent>
            </PageSection>
        </Page>
    );
}
